import Vue from 'vue';
import axios from 'axios'; /* TODO: Setup and use Axios API instance */
import { endpoint, API_BASE_URL, OFFER_API_ENDPOINTS, AVAILABILITY_API_ENDPOINTS } from '@/utils/api/endpoints';
import { Offer, OfferRequest } from '@/models/offer.model';
import moment from 'moment';

export default {
	namespaced: true,

	state: {
		offer: new Offer(),
		offers: [],
	},

	getters: {
		offer: state => state.offer,
		offers: state => state.offers.map(x => new Offer(x)),

		/* Company Getters */
		companyProfessionalApplications: (_, getters) => {
			const offers = getters.offers.filter(x => x.offers_requests && x.status === Offer.STATUS.REQUESTED && x.offer_type === Offer.TYPES.PUBLIC);

			const applications = [];
			for (let offer of offers) {
				if (!offer.offers_requests) continue;

				for (let request of offer.offers_requests) {
					if ([OfferRequest.STATUS.CANCELLED, OfferRequest.STATUS.REJECTED, OfferRequest.STATUS.CONFIRMED].includes(request.status)) continue;
					applications.push({ offer, request });
				}
			}

			return applications;
		},
		companyOffersPosted: (_, getters) => getters.offers.filter(x => x.offer_type === Offer.TYPES.PUBLIC && [Offer.STATUS.OPEN, Offer.STATUS.REQUESTED].includes(x.status)),
		companyBookingRequests: (_, getters, __, rootGetters) => {
			const offers = getters.offers.filter(x => x.offer_type === Offer.TYPES.PRIVATE && x.status === Offer.STATUS.REQUESTED);

			const requests = [];
			for (let offer of offers) {
				for (let request of offer.offers_requests) {
					if ([OfferRequest.STATUS.CANCELLED, OfferRequest.STATUS.REJECTED].includes(request.status)) continue;
					requests.push({ offer, request });
				}
			}

			return [...requests, ...rootGetters['availability/professionalRequestsReceived']];
		},

		/* Professional Getters */
		professionalApplicationsSent: (_, getters, rootState) => {
			const requests = [];

			for (let offer of getters.offers) {
				if (offer.offer_type === Offer.TYPES.PRIVATE) continue;
				if (!offer.offers_requests) continue;

				for (let request of offer.offers_requests) {
					if ([OfferRequest.STATUS.CANCELLED, OfferRequest.STATUS.REJECTED, OfferRequest.STATUS.CONFIRMED].includes(request.status)) continue;
					if (request.id === rootState.user.id) requests.push({ offer, request });
				}
			}

			return requests;
		},
		professionalOffersNearMe: (_, getters, { user }) => {
			const applications = getters.professionalApplicationsSent.map(x => x.offer.id);

			const yearsExperience = moment().diff(user.experience.start_date, 'years', false);

			const offers = getters.offers.filter(({ id, offer_type, status, practice_region, experience }) => {
				if (!(!applications.includes(id) && offer_type === Offer.TYPES.PUBLIC && (status === Offer.STATUS.OPEN || status === Offer.STATUS.REQUESTED))) return false;
				const locationMatch = user.locations.includes(practice_region);
				const experienceMatch = yearsExperience >= experience;
				return locationMatch && experienceMatch;
			});
			return offers;
		},
		professionalPrivateBookingRequests: (_, getters, rootState) => {
			const offers = getters.offers.filter(x => x.status === Offer.STATUS.REQUESTED);
			const requests = [];

			for (let offer of offers) {
				if (!offer.offers_requests || offer.offer_type !== Offer.TYPES.PRIVATE) continue;

				for (let request of offer.offers_requests) {
					if ([OfferRequest.STATUS.CANCELLED, OfferRequest.STATUS.REJECTED].includes(request.status)) continue;
					if (request.id === rootState.user.id) requests.push({ offer, request, offer_start_date: offer.date });
				}
			}

			return requests;
		},
		professionalPublicBookingRequests: (_, getters, rootState) => {
			const offers = getters.offers.filter(x => x.status === Offer.STATUS.REQUESTED);
			const requests = [];

			for (let offer of offers) {
				if (!offer.offers_requests || offer.offer_type !== Offer.TYPES.PUBLIC) continue;

				for (let request of offer.offers_requests) {
					if ([OfferRequest.STATUS.CANCELLED, OfferRequest.STATUS.REJECTED].includes(request.status)) continue;
					if (request.id === rootState.user.id) requests.push({ offer, request, offer_start_date: offer.date });
				}
			}

			return requests;
		},
	},

	mutations: {
		SET_PRACTICE_OFFER: (state, data) => (state.offer = data),
		SET_OFFERS: (state, data) => (state.offers = data),
		UPDATE_OFFERS: (state, data) => {
			if (!Array.isArray(data)) data = [data];
			for (let value of data) {
				const index = state.offers.findIndex(existing => existing.id === value.id);
				if (index == -1) {
					Vue.set(state.offers, state.offers.length, value);
				} else {
					Vue.set(state.offers, index, value);
				}
			}
		},
	},

	actions: {
		async _LoadOffers({ rootGetters }, { endPoint, params, query }) {
			if (!endPoint) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const url = endpoint(API_BASE_URL, endPoint, { params, query });
				const results = await axios.get(url, { headers: { Authorization: rootGetters.jwtToken } });
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load offers' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async LoadCompanyOffers({ commit, dispatch, rootGetters }, { companyId, startDate, endDate, query = null } = {}) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (!companyId) companyId = rootGetters.company.id;

			try {
				const results = await dispatch('_LoadOffers', {
					endPoint: OFFER_API_ENDPOINTS.READ_BY_COMPANY,
					query,
					params: {
						id: companyId,
						startDate,
						endDate,
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load offers' });

				commit('UPDATE_OFFERS', results.data.Items);
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async FetchFilteredOffers({ dispatch, rootGetters }, { companyId, startDate, endDate, offersType, query = null } = {}) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (!companyId) companyId = rootGetters.company.id;

			try {
				const results = await dispatch('_LoadOffers', {
					endPoint: OFFER_API_ENDPOINTS.READ_BY_COMPANY,
					query,
					params: {
						id: companyId,
						startDate,
						endDate: null, // Removed Load More button - LBDEV-639
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load offers' });

				let offers = results.data.Items;
				let filteredOffers = [];

				if (offersType === 'practice-booking-requests-sent') {
					offers = offers.filter(x => x.offer_type === Offer.TYPES.PRIVATE && x.status === Offer.STATUS.REQUESTED);

					for (let offer of offers) {
						for (let request of offer.offers_requests) {
							if ([OfferRequest.STATUS.CANCELLED, OfferRequest.STATUS.REJECTED].includes(request.status)) continue;
							filteredOffers.push({ offer, request });
						}
					}
				}

				if (offersType === 'practice-applications-received') {
					offers = offers.filter(x => x.offers_requests && x.status === Offer.STATUS.REQUESTED && x.offer_type === Offer.TYPES.PUBLIC);

					for (let offer of offers) {
						if (!offer.offers_requests) continue;

						for (let request of offer.offers_requests) {
							if ([OfferRequest.STATUS.CANCELLED, OfferRequest.STATUS.REJECTED, OfferRequest.STATUS.CONFIRMED].includes(request.status)) continue;
							filteredOffers.push({ offer, request });
						}
					}
				}

				if (offersType === 'practice-created-offers') {
					filteredOffers = offers.filter(x => x.offer_type === Offer.TYPES.PUBLIC && [Offer.STATUS.OPEN, Offer.STATUS.REQUESTED].includes(x.status));
				}

				return filteredOffers;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async SearchOffers({ commit, dispatch, rootGetters }, { profession, startDate, endDate, experience, query = null }) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (!profession) profession = rootGetters.user.profession;
			if (!experience) experience = 0;
			if (typeof startDate !== 'string' || typeof endDate !== 'string') return;
			try {
				const results = await dispatch('_LoadOffers', {
					endPoint: OFFER_API_ENDPOINTS.SEARCH,
					query,
					params: {
						profession,
						startDate,
						endDate,
						experience,
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load offers' });

				commit('UPDATE_OFFERS', results.data.Items);
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async FetchOffers({ dispatch, rootGetters, rootState }, { profession, startDate, endDate, experience, offersType, query = null }) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (!profession) profession = rootGetters.user.profession;
			if (!experience) experience = 0;

			try {
				const results = await dispatch('_LoadOffers', {
					endPoint: OFFER_API_ENDPOINTS.SEARCH,
					query,
					params: {
						profession,
						startDate,
						endDate,
						experience,
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load offers' });

				const offersResults = results.data.Items;
				const offers = offersResults.filter(x => x.status === Offer.STATUS.REQUESTED);
				let requests = [];
				let locumApplicationsSent = [];

				if (offersType === 'locum-booking-requests-received') {
					for (let offer of offers) {
						if (!offer.offers_requests || offer.offer_type !== Offer.TYPES.PRIVATE) continue;

						for (let request of offer.offers_requests) {
							if ([OfferRequest.STATUS.CANCELLED, OfferRequest.STATUS.REJECTED].includes(request.status)) continue;
							if (request.id === rootState.user.id) requests.push({ offer, request, offer_start_date: offer.date });
						}
					}
				}

				if (['locum-offers-near-me', 'locum-applications-sent'].includes(offersType)) {
					for (let offer of offersResults) {
						if (offer.offer_type === Offer.TYPES.PRIVATE) continue;
						if (!offer.offers_requests) continue;

						for (let request of offer.offers_requests) {
							if ([OfferRequest.STATUS.CANCELLED, OfferRequest.STATUS.REJECTED, OfferRequest.STATUS.CONFIRMED].includes(request.status)) continue;
							if (request.id === rootState.user.id) locumApplicationsSent.push({ offer, request });
						}
					}
				}

				if (offersType === 'locum-applications-sent') {
					requests = locumApplicationsSent;
				}

				if (offersType === 'locum-offers-near-me') {
					const yearsExperience = moment().diff(experience, 'years', false);

					const applications = locumApplicationsSent.map(x => x.offer.id);

					requests = offersResults.filter(({ id, offer_type, status, practice_region, experience }) => {
						if (!(!applications.includes(id) && offer_type === Offer.TYPES.PUBLIC && (status === Offer.STATUS.OPEN || status === Offer.STATUS.REQUESTED))) return false;
						const locationMatch = rootGetters.user.locations.includes(practice_region);
						const experienceMatch = yearsExperience >= experience;
						return locationMatch && experienceMatch;
					});
				}

				return requests;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async CreateOffer(_, offer) {
			if (!offer) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (Array.isArray(offer) && typeof offer !== 'object') throw window.$Error(window.$ERROR.ERR_TYPE, { parent: 'Offer can only be an object' });

			try {
				const url = endpoint(API_BASE_URL, OFFER_API_ENDPOINTS.CREATE);
				const results = await axios.post(url, offer);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to create or load offer' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpdateOffer(_, { offer, professionalId, date }) {
			if (!offer || !professionalId || !date) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (Array.isArray(offer) && typeof offer !== 'object') throw window.$Error(window.$ERROR.ERR_TYPE, { parent: 'Offer can only be an object' });

			try {
				const url = endpoint(API_BASE_URL, AVAILABILITY_API_ENDPOINTS.REQUEST_BOOKING, {
					params: {
						id: professionalId,
						date,
					},
				});
				const results = await axios.put(url, offer);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to update or load offer' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpsertOffer({ dispatch, commit }, payload) {
			if (!payload) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const result = await dispatch(payload.offer?.id ? 'UpdateOffer' : 'CreateOffer', payload);
				commit('UPDATE_OFFERS', result);
				return result;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async CreatePrivateOffer(_, offer) {
			if (!offer) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (Array.isArray(offer) && typeof offer !== 'object') throw window.$Error(window.$ERROR.ERR_TYPE, { parent: 'Offer can only be an object' });

			try {
				const url = endpoint(API_BASE_URL, OFFER_API_ENDPOINTS.PRIVATE_CREATE);
				const results = await axios.post(url, offer);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to create or load offer' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpdatePrivateOffer(_, offer) {
			if (!offer) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (Array.isArray(offer) && typeof offer !== 'object') throw window.$Error(window.$ERROR.ERR_TYPE, { parent: 'Offer can only be an object' });

			try {
				const url = endpoint(API_BASE_URL, OFFER_API_ENDPOINTS.PRIVATE_UPDATE);
				const results = await axios.put(url, offer);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to update or load offer' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpsertPrivateOffer({ dispatch, commit }, offer) {
			if (!offer) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const result = await dispatch(offer.id ? 'UpdatePrivateOffer' : 'CreatePrivateOffer', offer);
				commit('UPDATE_OFFERS', result);
				return result;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async ReadPublicOffer({ rootGetters }, offerID) {
			if (!offerID) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			try {
				const url = endpoint(API_BASE_URL, OFFER_API_ENDPOINTS.READ, {
					params: {
						id: offerID,
					},
				});
				const results = await axios.get(url, null, { headers: { Authorization: rootGetters.jwtToken } });
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to get offer' });
				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpdatePublicOffer(_, offer) {
			if (!offer) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			try {
				const url = endpoint(API_BASE_URL, OFFER_API_ENDPOINTS.UPDATE_DETAIL, {
					params: {
						id: offer.id,
					},
				});
				const results = await axios.post(url, offer);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to get offer' });
				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpdateApplicationToOffer(_, offer) {
			if (!offer) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			try {
				const url = endpoint(API_BASE_URL, OFFER_API_ENDPOINTS.UPDATE_APPLICATION, {
					params: {
						id: offer.id,
					},
				});
				const results = await axios.put(url, offer);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to get offer' });
				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},
	},
};
